import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { captureApiError } from "./errorTrail";

function dispatchApiEvent(method: string, url: string, status: number) {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(
      new CustomEvent("marketlink:api", {
        detail: { method: method.toUpperCase(), url, status },
      }),
    );
  } catch {
    // Ignore — old browsers / SSR
  }
}

async function throwIfResNotOk(res: Response) {
  if (res.ok) {
    return;
  }

  const contentType = res.headers.get("content-type") ?? "";
  let message = res.statusText || `Request failed with status ${res.status}`;
  let code: string | undefined;
  let body: unknown;

  try {
    const rawBody = await res.text();

    if (contentType.includes("application/json")) {
      const data = JSON.parse(rawBody) as { message?: unknown; error?: unknown; code?: unknown };
      body = data;
      if (typeof data.message === "string") {
        message = data.message;
      } else if (typeof data.error === "string") {
        message = data.error;
      } else if (rawBody.trim()) {
        message = rawBody.trim();
      }
      if (typeof data.code === "string") {
        code = data.code;
      }
    } else if (rawBody.trim()) {
      message = rawBody.trim();
    }
  } catch {
    // Ignore parse errors and fall back to the default message
  }

  captureApiError(res.url || "unknown", res.status, message);
  const err = new Error(message || `Request failed with status ${res.status}`);
  (err as any).status = res.status;
  if (code) (err as any).code = code;
  if (body !== undefined) (err as any).body = body;
  throw err;
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const isFormData = typeof FormData !== "undefined" && data instanceof FormData;

  const res = await fetch(url, {
    method,
    headers: data && !isFormData ? { "Content-Type": "application/json" } : {},
    body: isFormData ? data : data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  await throwIfResNotOk(res);
  dispatchApiEvent(method, url, res.status);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const url = queryKey.join("/") as string;
    const res = await fetch(url, {
      credentials: "include",
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    dispatchApiEvent("GET", url, res.status);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});

// Queries don't retry and never go stale, so anything that failed while the
// connection was down stays failed until something asks again — the screen sits
// on its error state long after the user is back on wifi. Retry just those, and
// only those: a query holding good data is left alone, so this can't become a
// refetch storm on a flaky connection.
if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    void queryClient.refetchQueries({
      predicate: (query) => query.state.status === "error",
    });
  });
}
